import * as React from "react";
import { cva } from "class-variance-authority";
import { Input, type InputProps } from "./input";
import { cn } from "../../lib/utils";

const labelVariants = cva(
  "ui-absolute ui-left-2.5 ui-text-gray-medium ui-transition-all ui-duration-200 ui-pointer-events-none",
  {
    variants: {
      state: {
        default: "ui-text-sm ui-top-[50%] ui-translate-y-[-50%]",
        shrunk: "ui-top-0 ui-translate-y-0 ui-text-text-xxs-medium",
      },
    },
    defaultVariants: {
      state: "default",
    },
  }
);

export interface BaseInputFieldProps
  extends Omit<
    InputProps,
    "label" | "variant" | "dimension" | "startAdornment" | "endAdornment"
  > {
  label?: string;
  variant?: "standard";
}

const BaseInputField = React.forwardRef<HTMLInputElement, BaseInputFieldProps>(
  ({ className, label, value, defaultValue, ...props }, ref) => {
    const [focused, setFocused] = React.useState(false);
    const [internalValue, setInternalValue] = React.useState(
      value ?? defaultValue ?? ""
    );
    const hasValue = Boolean(internalValue) || Boolean(value);
    const shouldShrinkLabel = focused || hasValue;

    // Handle controlled and uncontrolled inputs
    const inputValue = value !== undefined ? value : internalValue;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (value === undefined) {
        setInternalValue(e.target.value);
      }
      props.onChange?.(e);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      props.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      props.onBlur?.(e);
    };

    const inputId = props.id || `textfield-${React.useId()}`;
    const labelId = label ? `label-${inputId}` : undefined;
    const hasLabel = Boolean(label);

    return (
      <div className={cn("ui-relative ui-w-full", className)}>
        <Input
          {...props}
          id={inputId}
          variant="standard"
          ref={ref}
          value={inputValue}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          placeholder={
            hasLabel
              ? shouldShrinkLabel
                ? props.placeholder
                : undefined
              : props.placeholder
          }
          aria-labelledby={labelId}
          className={
            "ui-pt-2 ui-pb-1 ui-text-text-sm-regular ui-leading-normal"
          }
        />
        {label && (
          <label
            htmlFor={inputId}
            id={labelId}
            className={cn(
              labelVariants({
                state: shouldShrinkLabel ? "shrunk" : "default",
              }),
              focused && shouldShrinkLabel && "ui-text-primary-dark",
              props.disabled && "ui-opacity-50",
              props.error && "ui-text-red-dark"
            )}
          >
            {label}
            {props.required && " *"}
          </label>
        )}
      </div>
    );
  }
);

BaseInputField.displayName = "BaseInputField";

export { BaseInputField };
